import apiClient from './client';

export interface HealthDashboard {
  overview: {
    totalBirds: number;
    healthyBirds: number;
    birdsUnderObservation: number;
    birdsWithHealthConcerns: number;
  };
  statistics: {
    checklists: ChecklistStatistics;
    assessments: AssessmentStatistics;
    workflows: WorkflowStatistics;
  };
  healthDistribution: {
    healthy: number;
    underObservation: number;
    healthConcerns: number;
    critical: number;
    unknown: number;
  };
  trends: {
    checklists: TrendData[];
    assessments: TrendData[];
    period: string;
  };
  alerts: HealthAlert[];
  recommendations: HealthRecommendation[];
  lastUpdated: string;
}

export interface ChecklistStatistics {
  total: number;
  byType: Array<{ type: string; count: number }>;
  averageHealthScore: number;
  byStatus: Array<{ status: string; count: number }>;
  recent: any[];
}

export interface AssessmentStatistics {
  total: number;
  byStatus: Array<{ status: string; count: number }>;
  averageScore: number;
  completionRate: number;
  recent: any[];
}

export interface WorkflowStatistics {
  total: number;
  byStatus: Array<{ status: string; count: number }>;
  byDisease: Array<{ disease: string; count: number }>;
  bySeverity: Array<{ severity: string; count: number }>;
  active: any[];
}

export interface TrendData {
  date: string;
  averageScore: number;
  count: number;
}

export interface HealthAlert {
  id: string;
  type: 'OVERDUE_WORKFLOW' | 'DECLINING_HEALTH' | 'NO_RECENT_ASSESSMENT';
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  message: string;
  birdId: string;
  birdName: string;
  dueDate?: string;
  actionRequired: string;
}

export interface HealthRecommendation {
  id: string;
  type: 'ROUTINE_CARE' | 'PREVENTIVE_CARE' | 'ENVIRONMENTAL';
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  description: string;
  birds?: Array<{
    id: string;
    bandId: string;
    lastCheckup?: string;
  }>;
  actionRequired: string;
}

export interface BirdHealthReport {
  bird: {
    id: string;
    bandId: string;
    name: string;
    species: string;
    healthStatus: string;
    status: string;
  };
  recentChecklists: any[];
  recentAssessments: any[];
  activeWorkflows: any[];
  healthTrends: {
    checklists: Array<{
      date: string;
      score: number;
      status: string;
    }>;
    assessments: Array<{
      date: string;
      score: number;
      status: string;
    }>;
  };
  recommendations: HealthRecommendation[];
  lastUpdated: string;
}

export const healthAnalyticsApi = {
  // Get comprehensive health dashboard
  getDashboard: async (): Promise<HealthDashboard> => {
    const response = await apiClient.get<HealthDashboard>('/health-analytics/dashboard');
    return response.data;
  },

  // Get checklist statistics
  getChecklistStatistics: async (): Promise<ChecklistStatistics> => {
    const response = await apiClient.get<ChecklistStatistics>('/health-analytics/checklists/statistics');
    return response.data;
  },

  // Get assessment statistics
  getAssessmentStatistics: async (): Promise<AssessmentStatistics> => {
    const response = await apiClient.get<AssessmentStatistics>('/health-analytics/assessments/statistics');
    return response.data;
  },

  // Get workflow statistics
  getWorkflowStatistics: async (): Promise<WorkflowStatistics> => {
    const response = await apiClient.get<WorkflowStatistics>('/health-analytics/workflows/statistics');
    return response.data;
  },

  // Get health distribution
  getHealthDistribution: async (): Promise<{
    healthy: number;
    underObservation: number;
    healthConcerns: number;
    critical: number;
    unknown: number;
  }> => {
    const response = await apiClient.get<{
      healthy: number;
      underObservation: number;
      healthConcerns: number;
      critical: number;
      unknown: number;
    }>('/health-analytics/health-distribution');
    return response.data;
  },

  // Get recent trends
  getRecentTrends: async (days: number = 30): Promise<{
    checklists: TrendData[];
    assessments: TrendData[];
    period: string;
  }> => {
    const response = await apiClient.get<{
      checklists: TrendData[];
      assessments: TrendData[];
      period: string;
    }>('/health-analytics/trends', { days: days.toString() });
    return response.data;
  },

  // Get health alerts
  getHealthAlerts: async (): Promise<HealthAlert[]> => {
    const response = await apiClient.get<HealthAlert[]>('/health-analytics/alerts');
    return response.data;
  },

  // Get health recommendations
  getHealthRecommendations: async (): Promise<HealthRecommendation[]> => {
    const response = await apiClient.get<HealthRecommendation[]>('/health-analytics/recommendations');
    return response.data;
  },

  // Get bird health report
  getBirdHealthReport: async (birdId: string): Promise<BirdHealthReport> => {
    const response = await apiClient.get<BirdHealthReport>(`/health-analytics/bird/${birdId}/report`);
    return response.data;
  },

  // Get health metrics for charts
  getHealthMetrics: async (period: 'week' | 'month' | 'quarter' | 'year' = 'month') => {
    const days = {
      week: 7,
      month: 30,
      quarter: 90,
      year: 365
    }[period];

    const [trends, distribution, alerts] = await Promise.all([
      healthAnalyticsApi.getRecentTrends(days),
      healthAnalyticsApi.getHealthDistribution(),
      healthAnalyticsApi.getHealthAlerts()
    ]);

    return {
      trends,
      distribution,
      alerts,
      period
    };
  },

  // Get summary statistics
  getSummaryStats: async () => {
    const [dashboard, alerts, recommendations] = await Promise.all([
      healthAnalyticsApi.getDashboard(),
      healthAnalyticsApi.getHealthAlerts(),
      healthAnalyticsApi.getHealthRecommendations()
    ]);

    return {
      overview: dashboard.overview,
      alertCount: alerts.length,
      highPriorityAlerts: alerts.filter(a => a.severity === 'HIGH').length,
      recommendationCount: recommendations.length,
      highPriorityRecommendations: recommendations.filter(r => r.priority === 'HIGH').length,
    };
  }
};
